import {hasMatch} from 'fzy.js'
import {ValueValidator} from './root'
import {
  type ARIAFilterSuggestion,
  type AnyBlock,
  BlockType,
  type FilterKey,
  type MutableFilterBlock,
  type SuppliedFilterProviderOptions,
  type IndexedBlockValueItem,
} from '../types'
import {
  NO_VALUE,
  getFilterValue,
  getLastFilterBlockValue,
  getNoValueSuggestion,
  getUnescapedFilterValue,
  isMutableFilterBlock,
} from '../utils'

export class AsyncFilterProvider<T> extends ValueValidator<T> {
  endpoint: string
  repositoryScope: string | undefined
  abortController: AbortController
  valueValidator?: (value: string, data: T[] | null) => Partial<IndexedBlockValueItem> | false | null | undefined

  constructor(
    filterKey: FilterKey,
    endpoint: string,
    options?: SuppliedFilterProviderOptions,
    repositoryScope?: string,
  ) {
    super(filterKey, options)
    this.endpoint = endpoint
    this.repositoryScope = repositoryScope
    this.abortController = new AbortController()
  }

  abortPreviousRequests() {
    // If we had any previous requests that were still pending, terminate them and start a new one
    // This avoids race conditions with results coming back out of order
    this.abortController.abort()
    this.abortController = new AbortController()
  }

  async processSuggestions(
    filterBlock: AnyBlock | MutableFilterBlock,
    processSuggestion: (item: T, query: string) => ARIAFilterSuggestion,
    caretIndex?: number | null,
  ) {
    if (!this.shouldGetSuggestions(filterBlock)) return null

    const lastValue = getLastFilterBlockValue(filterBlock, caretIndex)
    const items = (await this.fetchSuggestions(filterBlock, lastValue)) ?? []
    const suggestions: ARIAFilterSuggestion[] = []

    if (hasMatch(lastValue, NO_VALUE) && this.options.filterTypes.valueless) {
      suggestions.push(getNoValueSuggestion(this.displayName, this.icon))
    }
    return [...suggestions, ...items.map(item => processSuggestion(item, lastValue))]
  }

  async fetchSuggestions(filterBlock: AnyBlock | MutableFilterBlock, filterValue?: string): Promise<T[] | null> {
    try {
      // In order to execute parallel async validations, we can't abort previous requests
      // this.abortPreviousRequests()
      const response = await this.fetchData(filterBlock, filterValue)
      if (!response || !response.ok) return null
      const json = await response.json()
      const dataKey = Object.keys(json)[0]
      return dataKey ? json[dataKey] : null
    } catch {
      return null
    }
  }

  async fetchData(filterBlock: AnyBlock | MutableFilterBlock, filterValue?: string): Promise<Response | undefined> {
    if (!filterBlock) return

    let rawQuery = filterBlock.raw
    if (!rawQuery) return

    const suggestionEndpoint = new URL(this.endpoint, window.location.origin)
    const params = suggestionEndpoint.searchParams || new URLSearchParams()

    if (this.repositoryScope && this.repositoryScope !== '') {
      const tokenToAdd = `repo:${this.repositoryScope}`
      if (!rawQuery.includes(tokenToAdd)) {
        rawQuery = `${rawQuery} ${tokenToAdd}`
      }
    }
    let value: string | null | undefined = filterValue
    value ??= isMutableFilterBlock(filterBlock)
      ? getFilterValue(filterBlock.value?.values.at(-1)?.value)
      : filterBlock.raw

    params.set('q', rawQuery)
    params.set('filter_value', getFilterValue(value) ?? '')
    suggestionEndpoint.search = params.toString()

    let response
    try {
      response = await fetch(suggestionEndpoint.toString(), {
        method: 'GET',
        mode: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        signal: this.abortController.signal,
      })
    } catch (error) {
      // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
      if (error.name !== 'AbortError') {
        throw error
      }
    }

    return response
  }

  async validateFilterBlockValues(values: IndexedBlockValueItem[]): Promise<IndexedBlockValueItem[]> {
    const valuesIndex: Record<number, IndexedBlockValueItem> = {}
    const validations = values.map(async (v, i) => {
      const extractedValue = getUnescapedFilterValue(v.value)

      // If there is a previous index of the value, we consider any subsequent indexes invalid as duplicates
      const prevIndex = values.findIndex(vi => getUnescapedFilterValue(vi.value) === extractedValue)
      if (!extractedValue || (prevIndex < i && prevIndex >= 0)) {
        valuesIndex[i] = {...v, valid: false}
        return
      }

      // Check if the value is the special "no value" value
      if (extractedValue === NO_VALUE && this.options.filterTypes.valueless) {
        valuesIndex[i] = {...v, valid: true, displayName: `No ${this.displayName}`}
        return
      }

      // Call fetch suggestions to get the value's data, if it exists
      const data = await this.fetchSuggestions({
        id: 1,
        type: BlockType.Filter,
        raw: `${this.key}:`,
        value: {
          raw: extractedValue,
          values: [{value: extractedValue}],
        },
      })

      const validatedData = this.validateValue?.(v, data)

      if (validatedData) {
        // We explicitly set the value to the extracted value here because the value returned from the server may be different
        valuesIndex[i] = {...v, valid: true, ...validatedData, value: v.value}
      } else {
        valuesIndex[i] = {...v, valid: false}
      }
    })

    await Promise.all(validations)

    return Object.values(valuesIndex)
  }
}
